import React, {useEffect, useRef, useState} from 'react';

import Form from 'react-bootstrap/Form';
import AsyncSelect from 'react-select/async';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {
    ChartFilterType,
    filterFreeTextTypes,
    filterNumericalTypes,
    filterSelectionTypes,
} from '@/views/storyboard/utils/chartConstants';
import useData from '@/utils/useData';
import sleep from '@/utils/sleep';
import {buildURL} from '@/utils/urlBuilder';
import {Field} from 'entities/chartFilters';
import {Tooltip} from '@/components/tooltips';
import {components, MultiValueProps, GroupBase} from 'react-select';
import i18n from 'i18n';



interface FormField {
    value: string;
    label?: string;
    type?: ChartFilterType;
}

interface Props {
    formField: FormField;
    setFieldValue: (fieldName: string, fieldValue: Field[]) => void;
    value?: Field[];
    widgetResetKey?: string;
    parent: string;
}

type EventFilterChoices<T extends string = string> = [T, T];

const SmartMultiInput = React.memo<Props>((
    {
        formField,
        setFieldValue,
        value = [],
        widgetResetKey = '',
        parent,
    },
) => {
    const [minValue, setMinValue] = useState<string>('*');
    const [maxValue, setMaxValue] = useState<string>('*');
    const [searchString, setSearchString] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const [widgetKeyChanged, setWidgetKeyChanged] = useState<boolean>(false);
    const [uniqueWidgetKey, _setUniqueWidgetKey] = useState<string>(String(Math.random()));
    const setUniqueWidgetKey = (): void => {
        _setUniqueWidgetKey(String(Math.random()));
        setWidgetKeyChanged(true);
    };
    const [componentKey, setComponentKey] = useState<string>(`${uniqueWidgetKey}-${widgetResetKey}`);

    const cleanValue = value.length ? value[0].value.replace('[', '').replace(']', '') : '';
    const cleanFieldName = formField.value.replace('all_fields.', '').replace('allFields.', '');

    const [filterChoices, loading] = useData<EventFilterChoices>(
        buildURL('eventFilterChoicesResource', {filter_name: cleanFieldName}),
        {
            useLocalStore: true,
            initialValue: [],
            requestParams: {
                params: {
                    q: searchString,
                },
            },
        },
    );

    const loadingRef = useRef<boolean>(loading);
    const filterRef = useRef<EventFilterChoices>(filterChoices);

    useEffect(() => {
        loadingRef.current = loading;
        filterRef.current = filterChoices;
    }, [
        loading,
        filterChoices,
    ]);

    useEffect(() => {
        setWidgetKeyChanged(true);
    }, [
        widgetResetKey,
    ]);

    useEffect(() => {
        if (widgetKeyChanged) {
            setComponentKey(`${uniqueWidgetKey}-${widgetResetKey}`);
            setWidgetKeyChanged(false);
        }
    }, [
        widgetKeyChanged,
        uniqueWidgetKey,
        widgetResetKey,
    ]);

    const getFilterOptions = async (inputValue: string = ''): Promise<Array<{label: string; value: string}>> => {
        const searchQuery = inputValue.toLowerCase();

        while (loadingRef.current) {
            await sleep(50);
        }

        if (searchQuery !== searchString) {
            setSearchString(searchQuery);
            loadingRef.current = true;

            while (loadingRef.current) {
                await sleep(50);
            }
        }

        const selectAllOption = {
            label: i18n.t('jt_st_select_all.text'),
            value: '*',
        };

        const filterOptions = filterRef.current.map((el) => ({
            value: el[0],
            label: el[1],
        }));

        const showSelectAllOption = !inputValue && (
            filterOptions.length
            && (filterOptions.length - value.length) > 1
        );

        return [
            ...(showSelectAllOption ? [selectAllOption] : []),
            ...filterOptions,
        ];
    };

    const getFilterOptionsDebounced = AwesomeDebouncePromise(getFilterOptions, 280);
    const filteredOptionsGetter = async (
        inputValue: string,
    ): Promise<Array<{label: string; value: string}>> => await getFilterOptionsDebounced(inputValue);

    const onNumericalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let eventValue = event.target.value;
        if (eventValue === '') {
            eventValue = '*';
        }
        const valueList = cleanValue ? cleanValue.split(' ') : [minValue, 'TO', maxValue];

        if (minValue !== '*') {
            valueList[0] = minValue;
        }
        if (maxValue !== '*') {
            valueList[2] = maxValue;
        }

        if (event.target.name === `${formField.value}_range_selector_min_value`) {
            valueList[0] = eventValue;
            setMinValue(eventValue);
        }
        if (event.target.name === `${formField.value}_range_selector_max_value`) {
            setMaxValue(eventValue);
            valueList[2] = eventValue;
        }

        setFieldValue(formField.value, [{value: `[${valueList.join(' ')}]`}]);
    };

    const onFeedbackTextSampleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue(formField.value, [{value: event.target.value}]);
    };

    const CustomMultiValue = (props: MultiValueProps<Field, true>) => {
        const {data} = props;

        return (
            <Tooltip
                content={data.label || ''}
                placement='top'
            >
                <div style={{display: 'inline-grid'}}>
                    <components.MultiValue {...props}>
                        {data.label}
                    </components.MultiValue>
                </div>
            </Tooltip>
        );
    };

    if ( formField.type&&filterSelectionTypes.includes(formField.type) ) {
        return (
            <AsyncSelect<Field, true, GroupBase<Field>>
                id={`${parent}-smart-multi-input-${cleanFieldName}`}
                className='event-filters-select'
                key={componentKey}
                value={value}
                isClearable
                isSearchable
                isMulti
                onChange={(v) => {
                    if (v && v.length && v[v.length - 1].value === '*') {
                        const filterOptions = filterRef.current.map((el) => ({
                            value: el[0],
                            label: el[1],
                        }));
                        setFieldValue(formField.value, filterOptions);
                    } else {
                        setFieldValue(formField.value, v ? v.concat() : []);
                    }

                    setUniqueWidgetKey();
                    setIsOpen(false);
                }}
                defaultMenuIsOpen={isOpen}
                name={formField.value}
                loadingMessage={() => i18n.t('jt_loading_opt.text')}
                isLoading={loading}
                defaultOptions
                loadOptions={filteredOptionsGetter}
                onMenuOpen={() => setIsOpen(true)}
                onMenuClose={() => setIsOpen(false)}
                components={{MultiValue: CustomMultiValue}}
            />
        );
    } else if (formField.type && filterNumericalTypes.includes(formField.type)) {
        let minValue: string | number = '';
        let maxValue: string | number = '';
        let hasValues = false;

        if (cleanValue) {
            const minRaw = cleanValue.split(' ')[0];
            const maxRaw = cleanValue.split(' ')[2];

            if (minRaw !== '*') {
                minValue = parseFloat(minRaw);
                hasValues = true;
            }
            if (maxRaw !== '*') {
                maxValue = parseFloat(maxRaw);
                hasValues = true;
            }
        }

        return (
            <div
                className="general-grid auto-height"
            >
                <Form.Control
                    data-cy={`${parent}-smart-multi-input-${cleanFieldName}-0`}
                    type="number"
                    className="width-5"
                    name={`${formField.value}_range_selector_min_value`}
                    value={minValue}
                    onChange={onNumericalChange}
                    placeholder={hasValues ? undefined : '2.3'}
                    key={`${componentKey}-min`}
                />
                <span className="mt-2 width-2">{i18n.t('to_text.text')}</span>

                <Form.Control
                    data-cy={`${parent}-smart-multi-input-${cleanFieldName}-1`}
                    type="number"
                    className="width-5"
                    name={`${formField.value}_range_selector_max_value`}
                    value={maxValue}
                    onChange={onNumericalChange}
                    placeholder={hasValues ? undefined : '8'}
                    key={`${componentKey}-max`}
                />
            </div>
        );
    } else if (formField.type && filterFreeTextTypes.includes(formField.type)) {
        return (
            <Form.Control
                data-cy={`${parent}-smart-multi-input-${cleanFieldName}`}
                key={componentKey}
                type="text"
                name={formField.value}
                value={value.length ? value[0].value : ''}
                onChange={onFeedbackTextSampleChange}
            />
        );
    }

    return null;
});


export default SmartMultiInput;
